/**
 * PatrolAttendance Component
 * 순찰용 출석부 - 룸별 좌석 현황 및 학생 정보 조회
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  BuildingOfficeIcon,
  UserIcon,
  XMarkIcon,
  ArrowPathIcon,
  PhoneIcon,
  ClockIcon,
  TicketIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { useRealTimeSeatManagement } from '../../../hooks/useRealTimeSeatManagement';
import { API_BASE_URL } from '../../../config/api';

// 룸 타입
interface Room {
  id: string;
  name: string;
  description?: string;
}

// 존 타입
interface Zone {
  id: string;
  name: string;
  color?: string;
  room_id?: string;
}

// 학생 상세 정보
interface StudentDetail {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  membership_type?: string;
  membership_expires?: string;
  check_in_time?: string;
  total_today_minutes?: number;
  seat_number?: number;
  notes?: string;
}

// 런타임 API URL 결정 함수
const getApiBaseUrl = (): string => {
  if (typeof window === 'undefined') return '';

  const host = window.location.hostname;
  const protocol = window.location.protocol;

  if (protocol === 'https:') {
    return '';
  }

  if (host.startsWith('192.168.')) {
    return `http://${host}:8000`;
  }

  if (API_BASE_URL) {
    return API_BASE_URL;
  }

  return '';
};

const PatrolAttendance: React.FC = () => {
  // 실시간 좌석 데이터
  const {
    seats,
    loading: seatsLoading,
    actions: { loadSeats },
  } = useRealTimeSeatManagement();

  // 상태
  const [rooms, setRooms] = useState<Room[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<string>('');
  const [loadingRooms, setLoadingRooms] = useState(false);

  // 학생 상세 모달
  const [selectedStudent, setSelectedStudent] = useState<StudentDetail | null>(null);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [loadingStudent, setLoadingStudent] = useState(false);

  // 룸 목록 로드
  const loadRooms = useCallback(async () => {
    setLoadingRooms(true);
    try {
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const apiUrl = getApiBaseUrl();
      const response = await fetch(`${apiUrl}/v1/owner/rooms`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRooms(data || []);
        // 첫 번째 룸 자동 선택
        if (data && data.length > 0 && !selectedRoomId) {
          setSelectedRoomId(data[0].id);
        }
      }
    } catch (error) {
      console.error('룸 목록 로드 오류:', error);
    } finally {
      setLoadingRooms(false);
    }
  }, [selectedRoomId]);

  // 존 목록 로드
  const loadZones = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const apiUrl = getApiBaseUrl();
      const response = await fetch(`${apiUrl}/v1/owner/zones`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setZones(data || []);
      }
    } catch (error) {
      console.error('존 목록 로드 오류:', error);
    }
  }, []);

  // 초기 로드
  useEffect(() => {
    loadRooms();
    loadZones();
  }, [loadRooms, loadZones]);

  // 학생 상세 정보 로드
  const loadStudentDetail = async (userId: string, seatNumber: number, checkInTime?: string) => {
    setLoadingStudent(true);
    try {
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const apiUrl = getApiBaseUrl();

      // 회원 정보 조회
      const response = await fetch(`${apiUrl}/v1/owner/members/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedStudent({
          id: data.id,
          name: data.name,
          phone: data.phone,
          email: data.email,
          membership_type: data.membership_type || data.active_membership?.type,
          membership_expires: data.active_membership?.expires_at,
          check_in_time: checkInTime,
          seat_number: seatNumber,
          notes: data.notes,
        });
      } else {
        // API 실패 시 기본 정보만 표시
        setSelectedStudent({
          id: userId,
          name: '정보 조회 실패',
          seat_number: seatNumber,
          check_in_time: checkInTime,
        });
      }
    } catch (error) {
      console.error('학생 정보 로드 오류:', error);
      setSelectedStudent({
        id: userId,
        name: '정보 조회 오류',
        seat_number: seatNumber,
        check_in_time: checkInTime,
      });
    } finally {
      setLoadingStudent(false);
      setShowStudentModal(true);
    }
  };

  // 좌석 클릭 핸들러
  const handleSeatClick = (seat: any) => {
    if (seat.current_session?.user) {
      loadStudentDetail(
        seat.current_session.user.id,
        seat.seat_number,
        seat.current_session.check_in_time
      );
    }
  };

  // 모달 닫기
  const closeStudentModal = () => {
    setShowStudentModal(false);
    setSelectedStudent(null);
  };

  // 선택된 룸의 좌석만 필터링
  const filteredSeats = selectedRoomId
    ? seats.filter((seat) => seat.room_id === selectedRoomId)
    : seats;

  // 좌석 번호순 정렬
  const sortedSeats = [...filteredSeats].sort((a, b) => a.seat_number - b.seat_number);

  // 존 색상 가져오기
  const getZoneColor = (zoneId?: string): string => {
    if (!zoneId) return 'bg-gray-100 border-gray-300';
    const zone = zones.find((z) => z.id === zoneId);
    if (!zone?.color) return 'bg-gray-100 border-gray-300';

    // 색상 코드를 Tailwind 클래스로 변환 (기본 색상들)
    const colorMap: Record<string, string> = {
      '#3B82F6': 'bg-blue-100 border-blue-400',
      '#10B981': 'bg-green-100 border-green-400',
      '#F59E0B': 'bg-yellow-100 border-yellow-400',
      '#EF4444': 'bg-red-100 border-red-400',
      '#8B5CF6': 'bg-purple-100 border-purple-400',
      '#EC4899': 'bg-pink-100 border-pink-400',
    };

    return colorMap[zone.color] || 'bg-gray-100 border-gray-300';
  };

  // 시간 포맷
  const formatTime = (isoString?: string): string => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 이용 시간 계산
  const getElapsedTime = (checkInTime?: string): string => {
    if (!checkInTime) return '-';
    const start = new Date(checkInTime);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;

    if (hours > 0) {
      return `${hours}시간 ${mins}분`;
    }
    return `${mins}분`;
  };

  // 현재 룸 정보
  const currentRoom = rooms.find((r) => r.id === selectedRoomId);
  const occupiedCount = sortedSeats.filter((s) => s.current_session).length;

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">순찰 출석부</h1>
            <p className="text-gray-600">룸별 좌석 현황 및 학생 확인</p>
          </div>
        </div>

        <button
          onClick={loadSeats}
          disabled={seatsLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-5 w-5 mr-2 ${seatsLoading ? 'animate-spin' : ''}`} />
          새로고침
        </button>
      </div>

      {/* 룸 선택 */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">룸 선택:</label>
            {loadingRooms ? (
              <div className="flex items-center">
                <ArrowPathIcon className="h-5 w-5 animate-spin text-gray-400 mr-2" />
                <span className="text-gray-500">로딩 중...</span>
              </div>
            ) : (
              <select
                value={selectedRoomId}
                onChange={(e) => setSelectedRoomId(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">전체 보기</option>
                {rooms.map((room) => (
                  <option key={room.id} value={room.id}>
                    {room.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {currentRoom && (
            <div className="text-sm text-gray-600">
              <span className="font-medium">{currentRoom.name}</span>
              {' - '}
              <span className="text-blue-600 font-semibold">{occupiedCount}명</span>
              <span className="text-gray-400"> / {sortedSeats.length}석</span>
            </div>
          )}
        </div>

        {/* 존 범례 */}
        {zones.length > 0 && (
          <div className="mt-4 pt-4 border-t flex flex-wrap gap-3">
            <span className="text-sm text-gray-500">구역:</span>
            {zones
              .filter((z) => !selectedRoomId || z.room_id === selectedRoomId)
              .map((zone) => (
                <div
                  key={zone.id}
                  className="flex items-center space-x-1"
                >
                  <div
                    className="w-4 h-4 rounded border-2"
                    style={{ backgroundColor: zone.color || '#gray', borderColor: zone.color || '#gray' }}
                  />
                  <span className="text-sm text-gray-600">{zone.name}</span>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* 좌석 그리드 */}
      <div className="bg-white p-6 rounded-lg shadow border">
        {sortedSeats.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {selectedRoomId ? '이 룸에 배치된 좌석이 없습니다.' : '좌석 데이터가 없습니다.'}
          </div>
        ) : (
          <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 gap-3">
            {sortedSeats.map((seat) => {
              const isOccupied = !!seat.current_session;
              const userName = seat.current_session?.user?.name || '';
              const zoneColorClass = getZoneColor(seat.zone_id);

              return (
                <div
                  key={seat.seat_number}
                  onClick={() => isOccupied && handleSeatClick(seat)}
                  className={`
                    relative p-3 rounded-lg border-2 text-center transition-all
                    ${isOccupied
                      ? `${zoneColorClass} cursor-pointer hover:shadow-md hover:scale-105`
                      : 'bg-gray-50 border-gray-200 cursor-default opacity-60'
                    }
                  `}
                >
                  {/* 좌석 번호 */}
                  <div className={`text-lg font-bold ${isOccupied ? 'text-gray-800' : 'text-gray-400'}`}>
                    {seat.seat_number}
                  </div>

                  {/* 학생 이름 */}
                  {isOccupied ? (
                    <div className="mt-1">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {userName}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {getElapsedTime(seat.current_session?.check_in_time)}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-1 text-xs text-gray-400">
                      빈좌석
                    </div>
                  )}

                  {/* 사용중 표시 */}
                  {isOccupied && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 학생 상세 모달 */}
      {showStudentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center">
                <UserIcon className="h-6 w-6 text-blue-600 mr-2" />
                학생 정보
              </h3>
              <button
                onClick={closeStudentModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {loadingStudent ? (
              <div className="flex items-center justify-center py-8">
                <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : selectedStudent ? (
              <div className="space-y-4">
                {/* 이름 및 좌석 */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-blue-900">
                    {selectedStudent.name}
                  </div>
                  <div className="text-blue-600 mt-1 flex items-center">
                    <MapPinIcon className="h-4 w-4 mr-1" />
                    {selectedStudent.seat_number}번 좌석
                  </div>
                </div>

                {/* 연락처 */}
                {selectedStudent.phone && (
                  <div className="flex items-center text-gray-700">
                    <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <span>{selectedStudent.phone}</span>
                  </div>
                )}

                {/* 입실 시간 */}
                <div className="flex items-center text-gray-700">
                  <ClockIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <span className="font-medium">입실:</span>{' '}
                    {formatTime(selectedStudent.check_in_time)}
                    <span className="text-gray-500 ml-2">
                      ({getElapsedTime(selectedStudent.check_in_time)} 이용중)
                    </span>
                  </div>
                </div>

                {/* 이용권 정보 */}
                {selectedStudent.membership_type && (
                  <div className="flex items-center text-gray-700">
                    <TicketIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <span className="font-medium">{selectedStudent.membership_type}</span>
                      {selectedStudent.membership_expires && (
                        <span className="text-gray-500 text-sm ml-2">
                          (~{new Date(selectedStudent.membership_expires).toLocaleDateString('ko-KR')})
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* 메모 */}
                {selectedStudent.notes && (
                  <div className="bg-yellow-50 p-3 rounded-lg">
                    <div className="text-sm font-medium text-yellow-800 mb-1">메모</div>
                    <div className="text-sm text-yellow-700">{selectedStudent.notes}</div>
                  </div>
                )}

                {/* 닫기 버튼 */}
                <button
                  onClick={closeStudentModal}
                  className="w-full mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  닫기
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default PatrolAttendance;
